import React, { useState, useEffect, useContext } from 'react';
import userContext from '../../context/users/UserContext';
import CustomBreadCrumb from '../../components/CustomBreadCrumb';
import { Button, Checkbox, DefaultButton, PrimaryButton, TextField } from '@fluentui/react';
import loaderContext from '../../context/loader/LoaderContext';
import { getUsersList, saveAdvUserList } from '../../service/AdvLicenseService';
import DialogBox from '../../components/Modal/DialogBox';
import { t } from 'i18next';
import toastContext from '../../context/toast/ToastContext';
import { TOAST_TYPE } from '../../constants/Constants';

export const breadCrumbItems = [
    {
        text: 'Advance Viewer License',
        key: 'masters',
    },
];


const AdvLicense = () => {

    const { roles, username, loadByUsername, authenticated } = useContext(userContext);
    const [isEditMode, setIsEditMode] = useState(false);
    const [userList, setUserList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState("");
    const [maxSelection, setMaxSelection] = useState(0);
    const { setLoaderRunning, ref } = useContext(loaderContext);
    const [noSelectionErrorMessage, setNoSelectionErrorMessage] = useState("");
    const [showCheckFirmDialog, setShowCheckFirmDialog] = useState(false);
    const [initialSelectedUsers, setInitialSelectedUsers] = useState(new Set(selectedUsers));
    const [refreshPage, setRefreshPage] = useState(false);
    const { showToastv2 } = useContext(toastContext);

    useEffect(() => {
        if (!authenticated) {
            console.log('--- Authentication again ----');
            loadByUsername();
            return;
        }
        fetchUserAccessList();
    }, [authenticated]);

    const fetchUserAccessList = async () => {
        setLoaderRunning(true);
        ref.current.continuousStart();
        try {
            const response = await getUsersList();
            console.log(response);
            if (response.status === 200 && response.data.result === "SUCCESS") {
                const fetchedUserList = response.data.data.userList || [];
                setUserList(fetchedUserList);
                setFilteredList(fetchedUserList);
                setInitialSelectedUsers(new Set(response.data.data.selectedUserIds || []));
                setSelectedUsers(new Set(response.data.data.selectedUserIds || []));
                setMaxSelection(response.data.data.maxSelectionAllowed);
                setLoaderRunning(false);
                ref.current.complete();
            } else {
                setLoaderRunning(false);
                ref.current.complete();
            }
        } catch (e) {
            console.log(e);
            setLoaderRunning(false);
        }
    };

    const filterBySearch = (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);

        if (searchValue) {
            let filtered;
            if (isEditMode) {
                filtered = userList.filter(user => user.username.toLowerCase().includes(searchValue.toLowerCase()));
            } else {
                filtered = userList.filter(user => selectedUsers.has(user.id) && user.username.toLowerCase().includes(searchValue.toLowerCase()));
            }
            setFilteredList(filtered);
        } else {
            setFilteredList(isEditMode ? userList : userList.filter(user => selectedUsers.has(user.id)));
        }
           
    };

    const isSelectAllChecked = filteredList.length > 0 && filteredList.every(user => selectedUsers.has(user.id));

    const handleCheckboxChange = (userId, isChecked) => {
        setSelectedUsers(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isChecked) {
                newSelected.add(userId);
            } else {
                newSelected.delete(userId);
            }
            return newSelected;
        });
    };

    const handleSelectAllChange = (isChecked) => {
        if (isChecked) {
            setSelectedUsers(prevSelected => {
                const newSelected = new Set(prevSelected);
                filteredList.forEach(user => newSelected.add(user.id));
                return newSelected;
            });
        } else {
            // Remove filtered users from the selection
            setSelectedUsers(prevSelected => {
                const newSelected = new Set(prevSelected);
                filteredList.forEach(user => newSelected.delete(user.id));
                return newSelected;
            });
        }
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
        if (!isEditMode) {
            setSearchTerm("");
            setFilteredList(userList);
            //setSelectedUsers(new Set(initialSelectedUsers));
        }
    };

    const handleCancel = () => {
        setSelectedUsers(new Set(initialSelectedUsers));
        // setIsEditMode(false);
        // setRefreshPage(true);
        // setSearchTerm("");
        setSearchTerm('');         // Clear the search term
        setFilteredList(userList);     // Reset to the full user list
        toggleEditMode();
    };

    const handleSubmit = async () => {
        if (maxSelection !== -1 && selectedUsers.size > maxSelection) {
            setNoSelectionErrorMessage(
                `You can only select up to ${maxSelection} users.`
            );
            setShowCheckFirmDialog(true);
        } else {
            const selectedUserIds = Array.from(selectedUsers);
            console.log("Selected User IDs:", selectedUserIds);
            setLoaderRunning(true);
            ref.current.continuousStart();
            try {
                const response = await saveAdvUserList(selectedUserIds);
                console.log(response);
                if (response.status === 200) {
                    if (response.data.result === "SUCCESS") {
                        showToastv2(TOAST_TYPE.SUCCESS, "Users saved successfully!")
                        setIsEditMode(false);
                        setInitialSelectedUsers(new Set(selectedUsers));
                    } else {
                        showToastv2(TOAST_TYPE.ERROR, response.data.message || "Failed to save users.");
                    }
                } else {
                    showToastv2(TOAST_TYPE.ERROR, response.data.message);
                }

            } catch (e) {
                console.error(e);
                showToastv2(TOAST_TYPE.ERROR, e.response.data.message || "An error occurred. Please try again.");
            } finally {
                setLoaderRunning(false);
                ref.current.complete();
            }

        }
    };

    return (
        <>
            {roles?.length > 0 && (

                <div className="main-container-body modal-scroll" style={{ height: '100%', overflow: 'auto' }}>
                    <div className="breadCrumb">
                        <CustomBreadCrumb items={breadCrumbItems} />
                    </div>
                    <div style={{ padding: '0px 3px', marginTop: '10px', height: 'calc(100% - 101px)' }}>
                        <div className='tab-section'>
                            <div className="ms-Grid" dir="ltr">
                                <div className="ms-Grid-row">
                                    <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg6 ms-xl10">
                                        <p className="form-heading" style={{ fontWeight: "bold" }}>
                                        {maxSelection === -1
                                                ? `${selectedUsers.size} advance viewer licenses allocated.`
                                                : `${selectedUsers.size} out of ${maxSelection} advance viewer licenses allocated.`}
                                        </p>
                                    </div>
                                    <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg6 ms-xl2">
                                        <TextField placeholder="Search by username" value={searchTerm} onChange={filterBySearch} />
                                    </div>
                                </div>

                                {!isEditMode ? (
                                    <>
                                        <div className="ms-Grid-row">
                                            <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg6 ms-xl12">
                                                {filteredList.length === 0 ? (
                                                    <p>No users available</p>
                                                ) : filteredList.map(user => (
                                                    selectedUsers.has(user.id) && (
                                                        <div key={user.id} style={{ border: "none", padding: "8px", textAlign: "left" }}>
                                                            {user.firstName + " " + user.lastName} ({user.username})
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                        <div className="left-side-button-container">
                                            <PrimaryButton onClick={toggleEditMode}>Edit</PrimaryButton>
                                        </div>
                                    </>

                                ) : (
                                    <>
                                        <div>
                                            <div className="checkbox-field ms-Grid-col ms-sm12 ms-md8 ms-lg6 ms-xl4">
                                                <Checkbox
                                                    label="Select All"
                                                    onChange={(e, isChecked) => handleSelectAllChange(isChecked)}
                                                    checked={isSelectAllChecked}
                                                />
                                            </div>
                                        </div>
                                        <div className="ms-grid-row">
                                            {filteredList.length === 0 ? (
                                                <p>No users available</p>
                                            ) : filteredList.map((user, index) => (
                                                <div key={index} className="checkbox-field ms-Grid-col ms-sm12 ms-md8 ms-lg6 ms-xl4">
                                                    <div style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>

                                                        <Checkbox
                                                            key={user.id}
                                                            label={`${user.firstName + "" + user.lastName} (${user.username})`}
                                                            checked={selectedUsers.has(user.id)}
                                                            onChange={(e, isChecked) => handleCheckboxChange(user.id, isChecked)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="ms-grid-row left-side-button-container">
                                            <PrimaryButton onClick={handleSubmit}>Submit</PrimaryButton>
                                            <DefaultButton onClick={handleCancel} style={{ marginLeft: "10px" }}>Cancel</DefaultButton>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DialogBox
                setShowDialogBox={setShowCheckFirmDialog}
                showDialogBox={showCheckFirmDialog}
                title={t("user.alert")}
                message={noSelectionErrorMessage}
                confirmButtonText={t("user.ok")}
                showOnlyOkButton={true}
            />
        </>
    );
};

export default AdvLicense;